import { ArrowDownIcon, ArrowsUpDownIcon, ArrowUpIcon } from "@heroicons/react/24/outline";
import { UseMutateFunction, useMutation, useQuery } from "@tanstack/react-query";
import { ListCommanWay, UpdateCommanWay } from "API/commonApi";
import { AxiosResponse } from "axios";
import { Chip } from "@heroui/react";
import { Table, TableHeader, TableBody, TableColumn, TableRow, TableCell } from "@heroui/table";
import moment from "moment";
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Select from "react-select";
import UseToast from "utils/AfToast";
import Spinner from "utils/Spinner";

const statusOptions = [
  { value: "Under Follow-up", label: "Under Follow-up" },
  { value: "Negotiation", label: "Negotiation" },
  { value: "On Hold", label: "On Hold" },
  { value: "Dropped", label: "Dropped" },
  { value: "Signed", label: "Signed" },
  { value: "Converted", label: "Converted" },
];

const Leads: React.FC<any> = (props) => {
  const navigator = useNavigate();
  const [tableData, setTableData] = useState<any>([]);
  const [isFirstLoad, setIsFirstLoad] = useState<any>(true);

  const [currentPage, serCurrentPage] = useState<number>(1);
  const [perPage, setPerPageCount] = useState<number>(25);
  const [pageCount, setPageCount] = useState<number>(1);
  const [itemCount, setItemCount] = useState<number>(1);

  const [filters, setFilters] = useState<any>({});
  const [isFilterChange, setIsFilterChange] = useState(false);
  const [sortStat, setSortStat] = useState<any>({});
  const [selectedStatuses, setSelectedStatuses] = useState<any>(
    statusOptions.filter((s) => s.value !== "Dropped") // Default: all except Dropped
  );

  const BASE_URL = process.env.REACT_APP_BACKEND_URL;
  const userD = (() => { const auth = localStorage.getItem("auth"); return auth ? JSON.parse(auth) : {}; })();
  const [userDetails, setUserDetails] = useState<any>(userD?.state?.user || {});

  const {
    mutate: updateLeadMutate,
  }: {
    mutate: UseMutateFunction<AxiosResponse<any, any>, unknown, any, unknown>;
    isLoading: any;
  } = useMutation((data) => UpdateCommanWay(data), {
    onSuccess: (data: any) => {
      if (data.status == "SUCCESS") {
        UseToast("Lead Updated Successfully", "success");
        performRefetch();
      } else {
        UseToast(data.message, "error");
      }
    },
    onError: (data: any) => {
      typeof data === "string" ? UseToast(data, "error") : UseToast(data.message, "error");
    },
  });

  const { error, isFetching, isError, isFetched, isLoading, refetch } = useQuery(
    ["_leads_list"],
    () => {
      const queryFilters = { ...filters };
      if (selectedStatuses && selectedStatuses.length > 0) {
        queryFilters.status = { $in: selectedStatuses.map((s: any) => s.value) };
      }
      return ListCommanWay("/admin/lead/list", queryFilters, {
        page: currentPage,
        paginate: perPage,
        order: [["id", "DESC"]],
      });
    },
    {
      keepPreviousData: true,
      onSuccess(data) {
        if (data.status == "SUCCESS") {
          setIsFirstLoad(false);
          setTableData(data?.data?.data || []);
          serCurrentPage(data.data.paginator?.currentPage);
          setPerPageCount(data.data.paginator?.perPage);
          setPageCount(data.data.paginator?.pageCount);
          setItemCount(data.data.paginator?.itemCount);
        }
      },
    }
  );

  const performRefetch = useCallback(() => {
    setTableData([]);
    refetch();
  }, [refetch]);

  useEffect(() => {
    if (isFilterChange) {
      serCurrentPage(1);
      performRefetch();
      setIsFilterChange(false);
    } else {
      performRefetch();
    }
  }, [currentPage, isFilterChange, performRefetch]);

  useEffect(() => {
    if (!isFirstLoad) {
      setIsFilterChange(true);
    }
  }, [filters]);

  useEffect(() => {
    if (!isFirstLoad) {
      setIsFilterChange(true);
    }
  }, [selectedStatuses]);

  const handleSort = (key) => {
    let data = [...tableData];
    let order = sortStat[key] == "asc" ? "desc" : "asc";

    let sortedData = data.sort((a, b) => {
      let valueA = key.split(".").reduce((obj, k) => obj?.[k], a) || "";
      let valueB = key.split(".").reduce((obj, k) => obj?.[k], b) || "";

      if (["createdAt", "updatedAt", "followupDate"].includes(key)) {
        const dateA = moment(valueA, "DD-MM-YYYY HH:mm:ss");
        const dateB = moment(valueB, "DD-MM-YYYY HH:mm:ss");
        if (!dateA.isValid() || !dateB.isValid()) {
          return 0;
        }
        return order === "asc" ? dateA.valueOf() - dateB.valueOf() : dateB.valueOf() - dateA.valueOf();
      }

      if (valueA < valueB) return order === "asc" ? -1 : 1;
      if (valueA > valueB) return order === "asc" ? 1 : -1;
      return 0;
    });

    let _sortStat = { ...sortStat };
    _sortStat[key] = order;
    setSortStat(_sortStat);
    setTableData(sortedData);
  };

  const GET_SORT_ICON = (key) => {
    switch (sortStat[key]) {
      case "asc":
        return <ArrowDownIcon className="h-4 inline-block cursor-pointer" onClick={() => handleSort(key)} />;
      case "desc":
        return <ArrowUpIcon className="h-4 inline-block cursor-pointer" onClick={() => handleSort(key)} />;
      default:
        return <ArrowsUpDownIcon className="h-4 inline-block cursor-pointer" onClick={() => handleSort(key)} />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case "new":
        return "primary";
      case "contacted":
        return "secondary";
      case "qualified":
        return "success";
      case "proposal":
        return "warning";
      case "negotiation":
        return "warning";
      case "converted":
        return "success";
      case "lost":
        return "danger";
      default:
        return "default";
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority + "") {
      case "3":
        return "danger";
      case "2":
        return "warning";
      case "1":
        return "primary";
      case "0":
        return "default";
      default:
        return "default";
    }
  };

  return (
    <>
      {isLoading && <Spinner />}

      <div className="flex justify-between items-center px-4 mb-2 mt-2">
        <h1 className="text-lg font-bold font-inter underline">Lead Management</h1>
        <div className="flex gap-3 items-center">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-gray-600">Status:</span>
            <Select
              isMulti
              options={statusOptions}
              value={selectedStatuses}
              onChange={setSelectedStatuses}
              placeholder="Filter by Status"
              className="text-sm min-w-[280px] z-50"
              classNamePrefix="select"
              isClearable
              closeMenuOnSelect={false}
            />
          </div>
          <button
            onClick={() => navigator("/af/leads/new")}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium text-sm"
          >
            + New Lead
          </button>
        </div>
      </div>

      <div className="px-4">
        <div className="flex font-inter">
          <Table
            isStriped
            isHeaderSticky
            removeWrapper
            className="w-full text-sm text-left text-gray-500"
            aria-label="leads table"
            classNames={{
              td: "group-data-[odd=true]/tr:before:bg-blue-100",
            }}
          >
            <TableHeader className="text-gray-700 uppercase bg-gray-50 sticky top-0">
              <TableColumn className="text-sm px-2 py-1 text-center">
                Lead
                <br />
                ID
              </TableColumn>
              <TableColumn className="text-sm px-2 py-1 text-center border-x border-gray-400">Generated By</TableColumn>
              <TableColumn className="text-sm px-2 py-1 text-center border-x border-gray-400">
                Created {GET_SORT_ICON("createdAt")}
              </TableColumn>
              {/*<TableColumn className="text-sm px-2 py-1 text-center border-x border-gray-400">
                Title {GET_SORT_ICON("title")}
                <input
                  type="search"
                  className="block p-1 text-sm text-gray-900 border border-gray-300 rounded-lg bg-gray-50 focus:ring-blue-500 focus:border-blue-500 mx-auto z-50"
                  placeholder="Search"
                  onKeyDown={(event: any) => {
                    if (event.key === "Enter") {
                      let _filters = { ...filters };
                      let val = event.target?.value;
                      if (!val) {
                        delete _filters["title"];
                      } else {
                        _filters["title"] = { $like: `%${val}%` };
                      }
                      setFilters(_filters);
                    }
                  }}
                />
              </TableColumn>*/}
              <TableColumn className="text-sm px-2 py-1 text-center border-x border-gray-400">
                Company {GET_SORT_ICON("company")}
                <input
                  type="search"
                  className="block p-1 text-sm text-gray-900 border border-gray-300 rounded-lg bg-gray-50 focus:ring-blue-500 focus:border-blue-500 mx-auto z-50"
                  placeholder="Search"
                  onKeyDown={(event: any) => {
                    if (event.key === "Enter") {
                      let _filters = { ...filters };
                      let val = event.target?.value;
                      if (!val) {
                        delete _filters["company"];
                      } else {
                        _filters["company"] = { $like: `%${val}%` };
                      }
                      setFilters(_filters);
                    }
                  }}
                />
              </TableColumn>
              <TableColumn className="text-sm px-2 py-1 text-center border-x border-gray-400">
                Contact Name {GET_SORT_ICON("contactPersonName")}
                <input
                  type="search"
                  className="block p-1 text-sm text-gray-900 border border-gray-300 rounded-lg bg-gray-50 focus:ring-blue-500 focus:border-blue-500 mx-auto z-50"
                  placeholder="Search"
                  onKeyDown={(event: any) => {
                    if (event.key === "Enter") {
                      let _filters = { ...filters };
                      let val = event.target?.value;
                      if (!val) {
                        delete _filters["contactPersonName"];
                      } else {
                        _filters["contactPersonName"] = { $like: `%${val}%` };
                      }
                      setFilters(_filters);
                    }
                  }}
                />
              </TableColumn>
              <TableColumn className="text-sm px-2 py-1 text-center border-x border-gray-400">Email/Phone</TableColumn>
              <TableColumn className="text-sm px-2 py-1 text-center border-x border-gray-400">Source {GET_SORT_ICON("source")}</TableColumn>
              <TableColumn className="text-sm px-2 py-1 text-center border-x border-gray-400">Status {GET_SORT_ICON("status")}</TableColumn>
              {/*<TableColumn className="text-sm px-2 py-1 text-center border-x border-gray-400">
                Priority {GET_SORT_ICON("priority")}
              </TableColumn>*/}
              <TableColumn className="text-sm px-2 py-1 text-center border-x border-gray-400">
                Follow Up {GET_SORT_ICON("followupDate")}
              </TableColumn>
              <TableColumn className="text-sm px-2 py-1 text-center">Actions</TableColumn>
            </TableHeader>

            <TableBody>
              {!tableData.length && (
                <TableRow>
                  <TableCell colSpan={10} className="text-center">
                    No Data Found
                  </TableCell>
                </TableRow>
              )}
              {tableData.map((rd, j) => {
                return (
                  <TableRow className={`${rd.isActive ? "" : "bg-red-200 text-black-900"}`} key={"tbl-tr-" + rd.id}>
                    <TableCell>{rd.id}</TableCell>
                    {/*<TableCell className="border-x border-gray-300">
                      <span className="font-semibold">{rd.title}</span>
                      {rd.desc && (
                        <>
                          <br />
                          <span className="text-xs text-gray-600">{rd.desc}</span>
                        </>
                      )}
                    </TableCell>*/}

                    <TableCell className="border-x border-gray-300">
                      <span className="text-black-900">{rd.LeadFollowups?.[0]?.note?.split("added by ")[1]?.split(" (")[0]}</span>
                    </TableCell>
                    <TableCell className="border-x border-gray-300 text-black-900">
                      {rd.createdAt?.split(" ")?.[0]}
                      <br />
                      {rd.createdAt?.split(" ")?.[1]}
                    </TableCell>
                    <TableCell className="border-x border-gray-300">{rd.company || "-"}</TableCell>
                    <TableCell className="border-x border-gray-300">
                      <span className="text-black-900">{rd.contactPersonName}</span>
                    </TableCell>

                    <TableCell className="border-x border-gray-300">
                      <div className="text-xs">
                        {rd.email && (
                          <>
                            <span className="font-semibold">E:</span> {rd.contactPersonEmail}
                            <br />
                          </>
                        )}
                        {rd.phone && (
                          <>
                            <span className="font-semibold">P:</span> {rd.contactPersonPhone}
                          </>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="border-x border-gray-300">{rd.source || "-"}</TableCell>
                    <TableCell className="text-center border-x border-gray-300">
                      <Chip color={getStatusColor(rd.status)} size="sm" variant="solid">
                        <span className="font-semibold px-2">{rd.status || "New"}</span>
                      </Chip>
                    </TableCell>
                    {/*<TableCell className="text-center border-x border-gray-300">
                      <Chip
                        color={getPriorityColor(rd.priority)}
                        size="sm"
                        variant="solid"
                      >
                        <span className="font-semibold px-2">{rd.priority || "Medium"}</span>
                      </Chip>
                    </TableCell>*/}
                    <TableCell className="border-x border-gray-300">
                      {rd.followupDate && rd.followupDate != "Invalid Date"
                        ? moment(rd.followupDate, "DD-MM-YYYY HH:mm:ss").format("DD-MM-YYYY")
                        : "-"}
                    </TableCell>

                    <TableCell className="font-semibold text-center">
                      <button
                        className="text-blue-600 hover:underline text-xs"
                        onClick={() => {
                          navigator("/af/leads/edit/" + rd.id, { state: { data: rd } });
                        }}
                      >
                        Edit
                      </button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>

        <nav className="flex items-center justify-between pt-4" aria-label="Table navigation">
          <span className="text-sm font-normal text-gray-500">
            Total: <span className="font-semibold text-gray-900">{itemCount}</span>
          </span>
          <ul className="inline-flex -space-x-px text-sm h-8">
            <li>
              <div
                onClick={() => serCurrentPage((prevData) => (prevData > 1 ? prevData - 1 : prevData))}
                className="flex items-center justify-center px-3 h-8 ml-0 leading-tight text-gray-500 bg-white border border-gray-300 rounded-l-lg hover:bg-gray-100 hover:text-gray-700 cursor-pointer"
              >
                Previous
              </div>
            </li>
            {Array.from({ length: pageCount }, (_, i) => i + 1).map((p) => (
              <li key={"paginate-" + p}>
                <button
                  onClick={() => serCurrentPage(p)}
                  type="button"
                  aria-current={currentPage === p ? "page" : undefined}
                  className={`flex items-center justify-center px-3 h-8 border border-gray-300
                          ${
                            currentPage === p
                              ? "text-blue-600 bg-blue-50 hover:bg-blue-100 hover:text-blue-700"
                              : "text-gray-500 bg-white hover:bg-gray-100 hover:text-gray-700"
                          }`}
                >
                  {p}
                </button>
              </li>
            ))}
            <li>
              <div
                onClick={() => serCurrentPage((prevData) => (prevData < pageCount ? prevData + 1 : prevData))}
                className="flex items-center justify-center px-3 h-8 leading-tight text-gray-500 bg-white border border-gray-300 rounded-r-lg hover:bg-gray-100 hover:text-gray-700 cursor-pointer"
              >
                Next
              </div>
            </li>
          </ul>
        </nav>
      </div>
    </>
  );
};

export default Leads;
